class GeminiService {
  async generateLearningContent(concept) {
    console.log(`Requesting content for: ${concept} from backend...`);

    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ concept }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP Error ${response.status}`);
      }

      const content = await response.json();
      console.log(`Successfully generated content for: ${concept}`);
      return content;
    } catch (error) {
      console.error('Error generating content:', error);

      // If the backend fails or API key is missing on the server, fallback to demo content
      console.log('Falling back to demo/generic content due to error:', error.message);
      return this.getDemoContent(concept);
    }
  }

  getDemoContent(concept) {
    const conceptLower = concept.toLowerCase();

    if (conceptLower.includes('gravity')) {
      return this.getGravityContent();
    } else if (conceptLower.includes('democracy')) {
      return this.getDemocracyContent();
    } else if (conceptLower.includes('dna')) {
      return this.getDNAContent();
    } else if (conceptLower.includes('automata')) {
      return this.getAutomataContent();
    }

    return this.getGenericContent(concept);
  }

  getGravityContent() {
    return {
      "explanation": "Gravity is like an invisible force that pulls everything toward each other! Think of it as Earth's way of giving everything a big hug, keeping us from floating away into space. Just like how a magnet pulls metal objects toward it, gravity pulls all objects with mass toward each other. The bigger and heavier something is, the stronger its gravitational pull. That's why we stick to Earth (it's really big!) and why the Moon orbits around us, and we orbit around the Sun.",
      "keyPoints": [
        "Gravity is a force that pulls objects with mass toward each other",
        "The more massive an object, the stronger its gravitational pull",
        "Gravity keeps us on Earth and prevents us from floating away",
        "It's responsible for keeping planets in orbit around the Sun",
        "Without gravity, there would be no atmosphere, oceans, or life as we know it"
      ],
      "flashcards": [
        { "question": "What is gravity?", "answer": "A force that pulls objects with mass toward each other" },
        { "question": "Why don't we float away from Earth?", "answer": "Because Earth's gravity pulls us toward its center" },
        { "question": "What determines how strong gravity is?", "answer": "The mass of the objects - bigger objects have stronger gravity" },
        { "question": "What keeps the Moon orbiting around Earth?", "answer": "Earth's gravitational pull" },
        { "question": "What would happen without gravity?", "answer": "Everything would float away and there would be no atmosphere or oceans" }
      ],
      "quiz": [
        {
          "question": "What causes gravity?",
          "options": ["Air pressure", "Mass of objects", "Magnetic fields", "Electric charges"],
          "correctAnswer": 1
        },
        {
          "question": "Which has stronger gravity?",
          "options": ["A basketball", "A car", "Earth", "A feather"],
          "correctAnswer": 2
        },
        {
          "question": "What keeps planets in orbit around the Sun?",
          "options": ["Solar wind", "Magnetic force", "Gravitational force", "Electric force"],
          "correctAnswer": 2
        },
        {
          "question": "If you drop a ball, which direction will it fall?",
          "options": ["Sideways", "Upward", "Toward Earth's center", "In circles"],
          "correctAnswer": 2
        },
        {
          "question": "Why is gravity weaker on the Moon than on Earth?",
          "options": ["The Moon is farther from the Sun", "The Moon has less mass than Earth", "The Moon has no atmosphere", "The Moon is colder than Earth"],
          "correctAnswer": 1
        }
      ]
    };
  }

  getDemocracyContent() {
    return {
      "explanation": "Democracy is like a classroom where everyone gets to vote on the rules and activities! Instead of just the teacher deciding everything, all students have a say in important decisions. In a democracy, people choose their leaders through voting, just like how you might vote for class president. The word 'democracy' comes from Greek words meaning 'people power' - it's a system where regular citizens have the power to make decisions about their government and laws, rather than having a king or dictator tell everyone what to do.",
      "keyPoints": [
        "People choose their leaders through free and fair elections",
        "Citizens have basic rights like freedom of speech and assembly",
        "Majority rule with protection for minority rights",
        "Government power comes from the consent of the people",
        "Regular elections allow people to change leaders peacefully"
      ],
      "flashcards": [
        { "question": "What does democracy mean?", "answer": "A system of government where people have the power to choose their leaders" },
        { "question": "How do people choose leaders in a democracy?", "answer": "Through voting in free and fair elections" },
        { "question": "What are some basic rights in a democracy?", "answer": "Freedom of speech, assembly, religion, and the press" },
        { "question": "What does 'majority rule' mean?", "answer": "The option that gets the most votes wins" },
        { "question": "Why are regular elections important?", "answer": "They allow people to peacefully change leaders if they're not satisfied" }
      ],
      "quiz": [
        {
          "question": "In a democracy, who has the ultimate power?",
          "options": ["The king", "The military", "The people", "The wealthy"],
          "correctAnswer": 2
        },
        {
          "question": "How often do democratic elections typically happen?",
          "options": ["Every month", "Every few years", "Only when needed", "Once in a lifetime"],
          "correctAnswer": 1
        },
        {
          "question": "What is a key feature of democratic elections?",
          "options": ["Only certain people can vote", "Voting is done in secret", "The government chooses the winner", "Elections happen randomly"],
          "correctAnswer": 1
        },
        {
          "question": "What protects minority rights in a democracy?",
          "options": ["The majority always decides", "Constitutional protections and laws", "The military", "Foreign governments"],
          "correctAnswer": 1
        },
        {
          "question": "Democracy is best compared to:",
          "options": ["A dictatorship", "A classroom where students vote on rules", "A monarchy", "A military command"],
          "correctAnswer": 1
        }
      ]
    };
  }

  getDNAContent() {
    return {
      "explanation": "DNA is like a recipe book inside every cell of your body! Just like how a cookbook contains instructions for making different dishes, DNA contains instructions for making you. Think of it as a super tiny instruction manual written in a special code that tells your body how to build everything - from your eye color to how tall you'll grow. Every person has their own unique DNA recipe book, which is why everyone looks different (except identical twins who share the same recipe book).",
      "keyPoints": [
        "DNA stands for Deoxyribonucleic Acid and contains genetic instructions",
        "It's found in the nucleus of almost every cell in your body",
        "DNA determines traits like eye color, height, and other characteristics",
        "It's made up of four chemical bases: A, T, G, and C",
        "DNA is passed from parents to children, which is why families look similar"
      ],
      "flashcards": [
        { "question": "What does DNA stand for?", "answer": "Deoxyribonucleic Acid" },
        { "question": "Where is DNA found in your body?", "answer": "In the nucleus of almost every cell" },
        { "question": "What does DNA determine about you?", "answer": "Physical traits like eye color, height, and other characteristics" },
        { "question": "How many chemical bases make up DNA?", "answer": "Four bases: A (Adenine), T (Thymine), G (Guanine), and C (Cytosine)" },
        { "question": "Why do children look like their parents?", "answer": "Because DNA is passed from parents to children" }
      ],
      "quiz": [
        {
          "question": "DNA is best compared to:",
          "options": ["A recipe book with instructions", "A computer hard drive", "A telephone", "A calculator"],
          "correctAnswer": 0
        },
        {
          "question": "Which part of the cell contains DNA?",
          "options": ["Cell membrane", "Cytoplasm", "Nucleus", "Mitochondria"],
          "correctAnswer": 2
        },
        {
          "question": "How many chemical bases are in DNA?",
          "options": ["Two", "Three", "Four", "Five"],
          "correctAnswer": 2
        },
        {
          "question": "Why do identical twins look the same?",
          "options": ["They eat the same food", "They share the same DNA", "They live in the same house", "They wear the same clothes"],
          "correctAnswer": 1
        },
        {
          "question": "What determines your eye color?",
          "options": ["The food you eat", "The air you breathe", "Your DNA", "The amount of sleep you get"],
          "correctAnswer": 2
        }
      ]
    };
  }

  getAutomataContent() {
    return {
      "explanation": "Automata are like smart machines that can make decisions based on rules! Think of them as robots with a simple brain that follows a set of instructions. Just like how a vending machine knows what to do when you insert coins and press buttons, automata follow predetermined rules to change from one state to another. They're used in computer science to understand how machines can process information and make decisions, similar to how traffic lights automatically change colors based on timers.",
      "keyPoints": [
        "Automata are mathematical models of machines that process information",
        "They have different states and transition between them based on input",
        "Finite automata have a limited number of states they can be in",
        "They're used in computer science for pattern recognition and language processing",
        "Real-world examples include vending machines, elevators, and traffic lights"
      ],
      "flashcards": [
        { "question": "What is an automaton?", "answer": "A mathematical model of a machine that processes information and changes states based on input" },
        { "question": "What are states in automata?", "answer": "Different conditions or situations the automaton can be in" },
        { "question": "How do automata make decisions?", "answer": "They follow predetermined rules to transition between states based on input" },
        { "question": "What is a real-world example of an automaton?", "answer": "A vending machine that changes states based on coins inserted and buttons pressed" },
        { "question": "What is finite automata?", "answer": "Automata with a limited, fixed number of possible states" }
      ],
      "quiz": [
        {
          "question": "What best describes an automaton?",
          "options": ["A machine that follows rules to process information", "A type of robot that walks around", "A computer program that plays games", "A mathematical equation"],
          "correctAnswer": 0
        },
        {
          "question": "Which is an example of an automaton in daily life?",
          "options": ["A bicycle", "A traffic light system", "A book", "A pencil"],
          "correctAnswer": 1
        },
        {
          "question": "What causes an automaton to change states?",
          "options": ["Random events", "Time passing", "Input according to rules", "External temperature"],
          "correctAnswer": 2
        },
        {
          "question": "Finite automata have:",
          "options": ["Unlimited memory", "A limited number of states", "No rules", "Infinite processing power"],
          "correctAnswer": 1
        },
        {
          "question": "Automata are primarily used in:",
          "options": ["Cooking recipes", "Computer science and mathematics", "Sports training", "Art creation"],
          "correctAnswer": 1
        }
      ]
    };
  }

  getGenericContent(concept) {
    const capitalizedConcept = concept.charAt(0).toUpperCase() + concept.slice(1);

    return {
      "explanation": `${capitalizedConcept} is an important concept that many people study and learn about. Like many complex topics, it has various aspects and applications that make it interesting and valuable to understand. Think of learning about ${concept} as building a foundation - each piece of knowledge connects to create a bigger picture. Just as you might learn to ride a bike by understanding balance, pedaling, and steering separately before putting it all together, understanding ${concept} involves learning its key components and how they work together.`,
      "keyPoints": [
        `${capitalizedConcept} is a significant topic in its field of study`,
        `Understanding ${concept} can help in various practical applications`,
        `There are multiple aspects and components to consider when learning about ${concept}`,
        `${capitalizedConcept} connects to other related concepts and ideas`,
        `Learning about ${concept} builds foundational knowledge for further study`
      ],
      "flashcards": [
        { "question": `What is ${concept}?`, "answer": `${capitalizedConcept} is an important concept that has various applications and aspects worth studying.` },
        { "question": `Why is ${concept} important?`, "answer": `Understanding ${concept} helps build knowledge and can be applied in various practical situations.` },
        { "question": `How can someone learn about ${concept}?`, "answer": `By studying its key components, understanding how they connect, and practicing with examples.` },
        { "question": `Where might you encounter ${concept}?`, "answer": `${capitalizedConcept} appears in academic study, practical applications, and related fields.` },
        { "question": `What makes ${concept} interesting to study?`, "answer": `Its various aspects, practical applications, and connections to other important concepts.` }
      ],
      "quiz": [
        {
          "question": `What is the best approach to learning about ${concept}?`,
          "options": ["Study its key components and how they connect", "Memorize definitions without understanding", "Ignore practical applications", "Focus only on one aspect"],
          "correctAnswer": 0
        },
        {
          "question": `Why is ${concept} considered important?`,
          "options": ["It's only useful for tests", "It has practical applications and builds knowledge", "It's easy to understand", "It never changes"],
          "correctAnswer": 1
        },
        {
          "question": `How does ${concept} relate to other topics?`,
          "options": ["It exists in complete isolation", "It only relates to one other topic", "It connects to various related concepts and ideas", "It has no connections to anything else"],
          "correctAnswer": 2
        },
        {
          "question": `What's a good way to understand ${concept} better?`,
          "options": ["Avoid asking questions", "Study examples and practice applications", "Only read about it once", "Focus on memorization alone"],
          "correctAnswer": 1
        },
        {
          "question": `Learning about ${concept} is valuable because:`,
          "options": ["It builds foundational knowledge for further learning", "It's required by law", "It's the easiest topic to study", "It never has practical uses"],
          "correctAnswer": 0
        }
      ]
    };
  }
}

const geminiServiceInstance = new GeminiService();
export default geminiServiceInstance;